import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Sidebar } from "./components/Sidebar";
import { ModuleGuard } from "./components/ModuleGuard";
import { AdminGuard } from "./components/AdminGuard";
import { Launcher } from "./modules/launcher/Launcher";
import { AdminPage } from "./modules/admin/AdminPage";
import { SmsModule } from "./modules/sms/SmsModule";
import { MembersModule } from "./modules/members/MembersModule";
import { VoiceModule } from "./modules/voice/VoiceModule";
import { ChatModule } from "./modules/chat/ChatModule";
import { FoodModule } from "./modules/food/FoodModule";
import { ReportsModule } from "./modules/reports/ReportsModule";
import { TipsModule } from "./modules/tips/TipsModule";
import { EventsModule } from "./modules/events/EventsModule";

function LoginScreen() {
  const { signIn, error } = useAuth();
  return (
    <div className="flex h-full items-center justify-center bg-tribal-bg p-4">
      <div className="w-full max-w-sm rounded-xl border border-tribal-border bg-tribal-surface p-8 text-center shadow-2xl">
        <div className="mb-4 text-5xl" aria-hidden>🪓</div>
        <h1 className="text-2xl font-bold tracking-tight">
          <span className="text-tribal-red">Tribal Axe</span> HQ
        </h1>
        <p className="mt-1 text-sm text-tribal-muted">Sign in to access your modules</p>

        <button
          type="button"
          onClick={signIn}
          className="mt-6 flex w-full items-center justify-center gap-3 rounded-lg bg-white px-4 py-3 font-medium text-gray-800 transition-colors hover:bg-gray-100"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Sign in with Google
        </button>

        {error && (
          <p className="mt-4 rounded-lg bg-red-900/20 px-3 py-2 text-sm text-red-400">{error}</p>
        )}

        <p className="mt-6 text-xs text-tribal-muted">Restricted to @tribalaxe.com accounts</p>
      </div>
    </div>
  );
}

function NotProvisioned() {
  const { signOut, sessionEmail } = useAuth();
  return (
    <div className="flex h-full items-center justify-center p-8 text-center">
      <div className="max-w-md rounded-lg border border-tribal-border bg-tribal-surface p-6">
        <h2 className="text-lg font-semibold">Account not provisioned</h2>
        <p className="mt-2 text-sm text-tribal-muted">
          {sessionEmail ? (
            <>
              <span className="font-mono text-white">{sessionEmail}</span> isn't set up in HQ yet.{" "}
            </>
          ) : null}
          Ask an admin to add you.
        </p>
        <button
          type="button"
          onClick={signOut}
          className="mt-4 rounded-md bg-white/5 px-3 py-1.5 text-sm hover:bg-white/10"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function Shell() {
  const { status, error, signOut } = useAuth();

  if (status === "loading") {
    return <div className="flex h-full items-center justify-center text-sm text-tribal-muted">Loading…</div>;
  }
  if (status === "signed-out") return <LoginScreen />;
  if (status === "not-provisioned") return <NotProvisioned />;
  if (status === "error") {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div className="max-w-md rounded-lg border border-red-500/40 bg-red-500/10 p-6">
          <h2 className="text-lg font-semibold text-red-200">Something went wrong</h2>
          <p className="mt-2 text-sm text-red-300">{error}</p>
          <button type="button" onClick={signOut} className="mt-4 rounded-md bg-white/5 px-3 py-1.5 text-sm">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 overflow-y-auto">
        <Routes>
          <Route path="/" element={<Launcher />} />
          <Route
            path="/admin"
            element={
              <ModuleGuard slug="admin">
                <AdminGuard>
                  <AdminPage />
                </AdminGuard>
              </ModuleGuard>
            }
          />
          <Route path="/sms/*"     element={<ModuleGuard slug="sms"><SmsModule /></ModuleGuard>} />
          <Route path="/members/*" element={<ModuleGuard slug="members"><MembersModule /></ModuleGuard>} />
          <Route path="/voice/*"   element={<ModuleGuard slug="voice"><VoiceModule /></ModuleGuard>} />
          <Route path="/chat/*"    element={<ModuleGuard slug="chat"><ChatModule /></ModuleGuard>} />
          <Route path="/food/*"    element={<ModuleGuard slug="food"><FoodModule /></ModuleGuard>} />
          <Route path="/reports"   element={<ModuleGuard slug="reports"><ReportsModule /></ModuleGuard>} />
          <Route path="/tips/*"    element={<ModuleGuard slug="tips"><TipsModule /></ModuleGuard>} />
          <Route path="/events/*" element={<ModuleGuard slug="events"><EventsModule /></ModuleGuard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </BrowserRouter>
  );
}
